import { Link } from 'react-router-dom';
import { fmtDate, parseLocalDate } from '@/lib/dates';
import { startMismatchSummary, type StartMismatch } from '@/lib/startAlignment';
import {
  PHASE_META, PHASE_STATUS_LABEL, phaseStatus,
  varianceWorkingDays, durationWeeks, laborStatus, startAfterEnd, effectiveStart,
  type ProjectPhase, type PhaseKey, type VarianceTone, type LaborStatus,
} from '@/lib/schedule';

const TONE_COLOR: Record<VarianceTone, string> = {
  green: 'var(--ew-green)',
  performance: 'var(--ew-performance)',
};

/** Small variance pill: "On time" (green) or "+Nd" (performance orange). */
function VariancePill({ v }: { v: { label: string; tone: VarianceTone } | null }) {
  if (!v) return null;
  const color = TONE_COLOR[v.tone];
  return (
    <span style={{
      marginLeft: 6, padding: '1px 6px', borderRadius: 'var(--ew-r-sm)',
      font: 'var(--ew-fw-semibold) 10px/1.5 var(--ew-font)',
      color, background: v.tone === 'green' ? 'var(--ew-green-50)' : 'var(--ew-performance-50)',
    }}>{v.label}</span>
  );
}

function DateCell({
  label, value, projected, variance, note,
}: {
  label: string; value: string | null; projected: boolean;
  variance?: { label: string; tone: VarianceTone } | null;
  /** Small qualifier under the date — provenance, or why it is blank. */
  note?: string | null;
}) {
  return (
    <div>
      <div className="pw-kpi-label" style={{ marginBottom: 3 }}>
        {label}{projected && value ? <span style={{ color: 'var(--ew-n-400)', fontWeight: 400 }}> (proj.)</span> : null}
      </div>
      <div style={{ font: 'var(--ew-fw-medium) var(--ew-fs-body-sm) var(--ew-font)', fontVariantNumeric: 'tabular-nums' }}>
        {fmtDate(value)}
        {value ? <VariancePill v={variance ?? null} /> : null}
      </div>
      {note ? (
        <div style={{ marginTop: 2, font: 'var(--ew-fw-regular) var(--ew-fs-meta) var(--ew-font)', color: 'var(--ew-n-400)' }}>
          {note}
        </div>
      ) : null}
    </div>
  );
}

const STATUS_TONE: Record<string, { bg: string; fg: string }> = {
  'in-progress': { bg: 'var(--ew-quality-50)', fg: 'var(--ew-quality)' },
  complete: { bg: 'var(--ew-green-50)', fg: 'var(--ew-green-900)' },
  'not-started': { bg: 'var(--ew-n-100)', fg: 'var(--ew-n-500)' },
};

function PhaseBlock({ phase, actualHours, actualsMapped }: { phase: ProjectPhase; actualHours: number | null; actualsMapped: boolean }) {
  const meta = PHASE_META[phase.phase as PhaseKey];
  const status = phaseStatus(phase);
  // Slip is measured against the baseline, and the PLANNED start is what a PM
  // moved — so that is the comparison worth showing. Before #380 there was no
  // planned start and this compared the baseline against `actualStart`, which
  // reported a re-plan as though the job had begun late.
  const startVar = varianceWorkingDays(phase.originalStart, phase.plannedStart);
  const endVar = varianceWorkingDays(phase.originalEnd, phase.actualEnd);
  // Duration follows whichever start the phase is actually working to.
  const effStart = effectiveStart(phase);
  const durAct = durationWeeks(effStart?.date ?? null, phase.actualEnd ?? phase.originalEnd);
  const durPlan = durationWeeks(phase.originalStart, phase.originalEnd);
  const st = STATUS_TONE[status];
  // Hours burned vs budget — the productivity signal (full PF/EAC lives in the
  // Productivity view).
  const budget = phase.budgetLaborHours;
  const labor = laborStatus(actualHours, budget, phase.completionPct ?? 0);
  const hoursPct = labor?.hoursPct ?? null;
  const overrunning = !!labor?.overrunning;

  return (
    <div style={{ border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: 'var(--ew-s-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ew-s-2)', marginBottom: 'var(--ew-s-3)' }}>
        <span style={{ width: 12, height: 12, borderRadius: 2, background: meta?.tick ?? 'var(--ew-green)' }} />
        <span style={{ font: 'var(--ew-fw-semibold) var(--ew-fs-body-ui) var(--ew-font)' }}>{meta?.label ?? phase.phase}</span>
        <span style={{ marginLeft: 'auto', padding: '2px 8px', borderRadius: 'var(--ew-r-sm)', font: 'var(--ew-fw-semibold) 10px/1.6 var(--ew-font)', letterSpacing: 'var(--ew-ls-caption)', textTransform: 'uppercase', background: st.bg, color: st.fg }}>
          {PHASE_STATUS_LABEL[status]}
        </span>
      </div>

      {/*
        The two dates a PM OWNS: the baseline, and the start they are currently
        working to. "Planned Start" carries the slip pill because that is the one
        they move.

        ⚠️ ACTUAL START IS NOT HERE, and its absence is deliberate (#482). It is
        Paycor-derived on a 'project' phase — the first day field hours were
        booked — so it belongs beside those hours on the Manpower tab, which is
        where it now lives. Asked directly, the PM who raised #460 and #482 did
        not want it on this card or on the projects list.

        ⚠️ It is still READ here: `phaseStatus` below reads `actualStart` and
        deliberately not `plannedStart`, which is what stopped 13 jobs on prod
        reading In Progress on a date that had not arrived (#380). The status pill
        above is that date speaking. It also remains editable in the schedule
        drawer, because clearing it is how a PM records "delayed indefinitely" —
        removing that would undo #394's fix, not tidy this card.
      */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--ew-s-3) var(--ew-s-4)' }}>
        <DateCell label="Original Start" value={phase.originalStart} projected={false} />
        <DateCell label="Original End" value={phase.originalEnd} projected={false} />
        <DateCell label="Planned Start" value={phase.plannedStart} projected={false} variance={startVar} />
        <DateCell label="Actual End" value={phase.actualEnd} projected={phase.isProjectedEnd} variance={endVar} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ew-s-2)', marginTop: 'var(--ew-s-3)', paddingTop: 'var(--ew-s-3)', borderTop: '1px solid var(--ew-n-100)', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)', color: 'var(--ew-n-500)' }}>
        <span>Duration <b style={{ color: 'var(--ew-dark)' }}>{durAct != null ? `${durAct} work wks` : '—'}</b>{durPlan != null ? ` / ${durPlan} work wks plan` : ''}</span>
        {endVar && endVar.tone === 'performance' ? <span style={{ marginLeft: 'auto' }}>Finish <VariancePill v={endVar} /></span> : null}
      </div>

      <div style={{ marginTop: 'var(--ew-s-2)', font: 'var(--ew-fw-regular) var(--ew-fs-meta) var(--ew-font)', color: 'var(--ew-n-400)' }}>
        {actualHours != null ? (
          <>
            Actual hours <b style={{ color: 'var(--ew-n-500)', fontVariantNumeric: 'tabular-nums' }}>{actualHours.toLocaleString()}</b>
            {hoursPct != null ? <> · <b style={{ color: 'var(--ew-n-500)' }}>{hoursPct}%</b> of {budget!.toLocaleString()} hr budget</> : null}
            {overrunning ? <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 'var(--ew-r-sm)', font: 'var(--ew-fw-semibold) 10px/1.5 var(--ew-font)', color: 'var(--ew-performance)', background: 'var(--ew-performance-50)' }}>hours ahead of progress</span> : null}
            {actualsMapped ? '' : ' · whole-job (map cost codes to split)'}
          </>
        ) : '— actuals'}
      </div>
    </div>
  );
}

// ── Timeline ────────────────────────────────────────────────────────────────

function monthsBetween(start: Date, end: Date): Date[] {
  const out: Date[] = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cur <= last) {
    out.push(new Date(cur));
    cur.setMonth(cur.getMonth() + 1);
  }
  return out;
}

function PhaseTimeline({ phases }: { phases: ProjectPhase[] }) {
  const dates: Date[] = [];
  for (const p of phases) {
    // plannedStart included, or a job re-planned past its original window draws
    // a bar running off the right-hand edge of its own axis (#380).
    for (const iso of [p.originalStart, p.plannedStart, p.originalEnd, p.actualStart, p.actualEnd]) {
      const d = parseLocalDate(iso);
      if (d) dates.push(d);
    }
  }
  if (dates.length < 2) return null;

  const min = new Date(Math.min(...dates.map((d) => d.getTime())));
  const max = new Date(Math.max(...dates.map((d) => d.getTime())));
  // Snap to month boundaries for clean gridlines.
  const spanStart = new Date(min.getFullYear(), min.getMonth(), 1);
  const spanEnd = new Date(max.getFullYear(), max.getMonth() + 1, 0);
  const total = spanEnd.getTime() - spanStart.getTime() || 1;
  const x = (d: Date | null) => (d ? Math.max(0, Math.min(100, ((d.getTime() - spanStart.getTime()) / total) * 100)) : null);

  const months = monthsBetween(spanStart, spanEnd);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayX = today >= spanStart && today <= spanEnd ? x(today) : null;

  const ROW_H = 40;

  return (
    <div style={{ padding: 'var(--ew-s-4)', borderBottom: '1px solid var(--ew-n-100)' }}>
      {/* Month header */}
      <div style={{ position: 'relative', height: 18, marginBottom: 4 }}>
        {months.map((m, i) => (
          <span key={i} style={{ position: 'absolute', left: `${x(m)}%`, font: 'var(--ew-fw-medium) 10px/1 var(--ew-font)', letterSpacing: 'var(--ew-ls-caption)', textTransform: 'uppercase', color: 'var(--ew-n-400)' }}>
            {m.toLocaleDateString('en-US', { month: 'short' })}
          </span>
        ))}
        {todayX != null ? (
          <span style={{ position: 'absolute', left: `${todayX}%`, transform: 'translateX(-50%)', font: 'var(--ew-fw-semibold) 10px/1 var(--ew-font)', color: 'var(--ew-danger)' }}>
            Today
          </span>
        ) : null}
      </div>

      {/* Track */}
      <div style={{ position: 'relative', height: phases.length * ROW_H }}>
        {/* Month gridlines */}
        {months.map((m, i) => (
          <div key={i} style={{ position: 'absolute', left: `${x(m)}%`, top: 0, bottom: 0, width: 1, background: 'var(--ew-n-100)' }} />
        ))}
        {/* Today line */}
        {todayX != null ? <div style={{ position: 'absolute', left: `${todayX}%`, top: 0, bottom: 0, width: 2, background: 'var(--ew-danger)' }} /> : null}

        {phases.map((p, i) => {
          const meta = PHASE_META[p.phase as PhaseKey];
          const planL = x(parseLocalDate(p.originalStart));
          const planR = x(parseLocalDate(p.originalEnd));
          // The live bar follows the start the phase is WORKING TO — actual if
          // work began, else the current plan, else the baseline (#380).
          const actL = x(parseLocalDate(effectiveStart(p)?.date ?? null));
          const actR = x(parseLocalDate(p.actualEnd ?? p.originalEnd));
          const status = phaseStatus(p);
          const barLabel = status === 'not-started' ? 'PROJECTED' : `${p.completionPct ?? 0}%`;
          return (
            <div key={p.phase} style={{ position: 'absolute', top: i * ROW_H, left: 0, right: 0, height: ROW_H }}>
              <span style={{ position: 'absolute', left: 0, top: 8, font: 'var(--ew-fw-semibold) 11px/1 var(--ew-font)', color: 'var(--ew-dark)' }}>{meta?.label ?? p.phase}</span>
              {/* PLAN bar (dashed) */}
              {planL != null && planR != null ? (
                <div style={{ position: 'absolute', left: `${planL}%`, width: `${Math.max(1, planR - planL)}%`, top: 2, height: 10, border: '1px dashed var(--ew-n-400)', borderRadius: 'var(--ew-r-sm)', background: 'var(--ew-n-50)' }}>
                  <span style={{ position: 'absolute', left: 4, top: -1, font: 'var(--ew-fw-medium) 8px/1.4 var(--ew-font)', color: 'var(--ew-n-400)' }}>PLAN</span>
                </div>
              ) : null}
              {/* Actual / projected bar */}
              {actL != null && actR != null ? (
                <div style={{ position: 'absolute', left: `${actL}%`, width: `${Math.max(2, actR - actL)}%`, top: 15, height: 16, background: meta?.tick ?? 'var(--ew-green)', borderRadius: 'var(--ew-r-sm)', opacity: status === 'not-started' ? 0.55 : 1, display: 'flex', alignItems: 'center', paddingLeft: 6 }}>
                  <span style={{ font: 'var(--ew-fw-semibold) 9px/1 var(--ew-font)', color: '#fff', letterSpacing: status === 'not-started' ? 'var(--ew-ls-caption)' : 0 }}>{barLabel}</span>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 'var(--ew-s-4)', marginTop: 'var(--ew-s-3)', font: 'var(--ew-fw-regular) 11px var(--ew-font)', color: 'var(--ew-n-500)' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 20, height: 8, borderRadius: 2, border: '1px dashed var(--ew-n-400)' }} />Original plan</span>
        {phases.map((p) => {
          const meta = PHASE_META[p.phase as PhaseKey];
          return <span key={p.phase} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 20, height: 8, borderRadius: 2, background: meta?.tick }} />{meta?.label} actual</span>;
        })}
      </div>
    </div>
  );
}

// ── Crew-schedule link ────────────────────────────────────────────────────────

/**
 * Where this card's "Manpower schedule" links point: the crew grid, scoped so
 * that THIS job's row is on it.
 *
 * ⚠️ A bare `/manpower` is wrong for a fab job. With no `?scope=`, the grid
 * opens on Self-Perform for anyone who is not a fab-only editor (#260), and
 * Self-Perform excludes the fabrication division outright — so a PM following
 * either alert from a fab job arrived on a page without the row the alert was
 * about. An explicit param wins over the viewer's default for every role
 * (`resolveScope`), which is what makes this link land the same way whoever
 * clicks it. Every other job is on the default view, so it keeps the clean URL.
 */
function crewScheduleHref(fab: boolean): string {
  return fab ? '/manpower?scope=fab' : '/manpower';
}

// ── Slip alert ────────────────────────────────────────────────────────────────

function SlipAlert({ phases, fab }: { phases: ProjectPhase[]; fab: boolean }) {
  const slips = phases
    .map((p) => ({ p, v: varianceWorkingDays(p.originalEnd, p.actualEnd) }))
    .filter((r) => r.v && r.v.days > 0);
  if (!slips.length) return null;

  const worst = slips.reduce((a, b) => (b.v!.days > a.v!.days ? b : a));
  const label = PHASE_META[worst.p.phase as PhaseKey]?.label ?? worst.p.phase;
  const install = phases.find((p) => p.phase === 'installation');
  // The start install is WORKING TO, not specifically its actual start — a fab
  // slip pushes the plan, and installation usually has not begun yet, so keying
  // this off `actualStart` alone suppressed the clause exactly when it was the
  // point (#380).
  const installStart = install ? effectiveStart(install)?.date ?? null : null;
  const pushMsg = worst.p.phase === 'fabrication' && installStart
    ? `, pushing installation start to ${fmtDate(installStart)}`
    : '';

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--ew-s-2)', padding: 'var(--ew-s-3) var(--ew-s-4)', margin: 'var(--ew-s-4)', borderRadius: 'var(--ew-r-md)', background: 'var(--ew-performance-50)', borderLeft: '3px solid var(--ew-performance)' }}>
      <span aria-hidden style={{ color: 'var(--ew-performance)' }}>⚠</span>
      <span style={{ font: 'var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font)', color: 'var(--ew-dark)' }}>
        {label} is running <b>+{worst.v!.days} working days</b> long{pushMsg}. Crew impact is on the{' '}
        {/* ⚠️ `Link`, not `<a href>`. Both cross-links out of this card were raw
            anchors, which full-reload the SPA: the React tree is torn down, every
            query cache is discarded and MSAL re-initialises, for a navigation
            that should be instant. The mismatch alert below copied the pattern
            from here, which is why both were converted together rather than
            leaving one looking correct beside the other. Both carry the scope
            for the same reason (crewScheduleHref). */}
        <Link to={crewScheduleHref(fab)} style={{ color: 'var(--ew-performance)', fontWeight: 600 }}>Manpower schedule</Link>.
      </span>
    </div>
  );
}

// ── Start / crew-schedule mismatch alert ─────────────────────────────────────

/**
 * The crew schedule and this card disagree about when the job starts (#385).
 *
 * Job 10943 is the shape it was found in: crew booked from 2026-09-28 against a
 * planned start of 2026-10-05. Two people own the two dates — the PM owns this
 * card, the Scheduler owns the crew grid — and neither screen showed the other's
 * answer, so a seven-day disagreement sat there with both of them right as far
 * as they could see.
 *
 * ⚠️ IT NAMES BOTH DATES AND BLAMES NEITHER. #385 asked for "an automation that
 * forces alignment", and deriving one date from the other would be the wrong
 * answer to it: the two fields have different owners and different permissions,
 * so a silent reconcile would let either role overwrite the other's work without
 * knowing it had. The same flag is on the crew grid row, so both people see it.
 *
 * The rule lives in lib/startAlignment, shared with that row — a flag that
 * appeared on one screen and not the other would be worse than neither.
 */
function StartMismatchAlert({ mismatch, fab }: { mismatch: StartMismatch; fab: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--ew-s-2)', padding: 'var(--ew-s-3) var(--ew-s-4)', margin: 'var(--ew-s-4)', borderRadius: 'var(--ew-r-md)', background: '#fff1f2', borderLeft: '3px solid #e11d48' }}>
      <span aria-hidden style={{ color: '#e11d48' }}>⚠</span>
      <span style={{ font: 'var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font)', color: 'var(--ew-dark)' }}>
        {startMismatchSummary(mismatch, fmtDate)}{' '}
        Either the planned start here or the crew days on the{' '}
        {/* "The same flag is on that row" is only true if the link lands on a
            scope that HAS the row — on a fab job, that is the Fabrication lens. */}
        <Link to={crewScheduleHref(fab)} style={{ color: '#e11d48', fontWeight: 600 }}>Manpower schedule</Link>{' '}
        needs to move — the same flag is on that row.
      </span>
    </div>
  );
}

// ── Start-after-end alert ────────────────────────────────────────────────────

/**
 * The job's start is later than the end it is working to.
 *
 * This is #394 on job 10965: delayed pending design changes, so the PM moved the
 * start to September while the completion stayed in July — and the projects list
 * rendered an end before its own start, with nothing anywhere saying so.
 *
 * ⚠️ #380 gave that start a proper home (`plannedStart`); it cannot supply the
 * matching end, because only a PM knows when a re-planned job now finishes. So
 * this asks, rather than deriving a coherent-looking date nobody stands behind.
 * That is the whole difference between the old behaviour and this one: the
 * contradiction is now stated at the point of entry instead of being silently
 * rendered on the list.
 *
 * Deliberately a WARNING, not a block. "Delayed, start moved, finish not yet
 * re-planned" is a real state a PM must be able to record — #380's acceptance
 * says so explicitly — it just must not be recorded silently.
 */
function StartAfterEndAlert({ flag, phaseLabel, canEdit, onEdit }: {
  flag: { start: string; end: string; kind: 'actual' | 'planned' | 'original' };
  phaseLabel: string;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const startWord = flag.kind === 'actual' ? 'actual start' : flag.kind === 'planned' ? 'planned start' : 'original start';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ew-s-2)', flexWrap: 'wrap', padding: 'var(--ew-s-3) var(--ew-s-4)', margin: 'var(--ew-s-4)', borderRadius: 'var(--ew-r-md)', background: 'var(--ew-performance-50)', borderLeft: '3px solid var(--ew-performance)' }}>
      <span aria-hidden style={{ color: 'var(--ew-performance)' }}>⚠</span>
      <span style={{ font: 'var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font)', color: 'var(--ew-dark)', flex: 1, minWidth: 220 }}>
        The {phaseLabel} {startWord} of <b>{fmtDate(flag.start)}</b> is after its completion of <b>{fmtDate(flag.end)}</b>.
        The Projects list shows both, so this job currently reads as finishing before it begins — the finish needs re-planning too.
      </span>
      {canEdit ? (
        <button className="ew-btn-outline" onClick={onEdit} style={{ borderColor: 'var(--ew-performance)', color: 'var(--ew-performance)', whiteSpace: 'nowrap' }}>
          Set the completion
        </button>
      ) : null}
    </div>
  );
}

// ── Labor over-run alert (prominent) ────────────────────────────────────────

function LaborAlert({ labor }: { labor: LaborStatus }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--ew-s-2)', padding: 'var(--ew-s-3) var(--ew-s-4)', margin: 'var(--ew-s-4)', borderRadius: 'var(--ew-r-md)', background: 'var(--ew-performance-50)', borderLeft: '3px solid var(--ew-performance)' }}>
      <span aria-hidden style={{ color: 'var(--ew-performance)', fontSize: 16, lineHeight: 1.2 }}>⚠</span>
      <span style={{ font: 'var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font)', color: 'var(--ew-dark)' }}>
        <b>Labor over-running.</b> {labor.hoursPct}% of budget hours consumed at {labor.completionPct}% complete
        {' '}(+{labor.gap} pts) — trending over budget. Full performance factor is on the Productivity view.
      </span>
    </div>
  );
}

// ── Mob-end divergence alert ─────────────────────────────────────────────────
// A mobilization runs past the planned end. The plan is the PM's statement, so
// it is never rewritten automatically (effectiveCompletion already supersedes
// it for display) — this flags the divergence and makes accepting it one click.

export interface MobEndFlag { mobEnd: string; phase: PhaseKey | string; planEnd: string }

function MobEndAlert({ flag, canEdit, onAccept, accepting }: {
  flag: MobEndFlag; canEdit: boolean; onAccept?: () => void; accepting?: boolean;
}) {
  const phaseLabel = PHASE_META[flag.phase as PhaseKey]?.label ?? flag.phase;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ew-s-2)', flexWrap: 'wrap', padding: 'var(--ew-s-3) var(--ew-s-4)', margin: 'var(--ew-s-4)', borderRadius: 'var(--ew-r-md)', background: 'var(--ew-performance-50)', borderLeft: '3px solid var(--ew-performance)' }}>
      <span aria-hidden style={{ color: 'var(--ew-performance)' }}>⚠</span>
      <span style={{ font: 'var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font)', color: 'var(--ew-dark)', flex: 1, minWidth: 220 }}>
        A mobilization runs through <b>{fmtDate(flag.mobEnd)}</b> — past the planned {phaseLabel} end of {fmtDate(flag.planEnd)}. The Target Completion KPI already follows the mobilization; the schedule below still shows the old plan.
      </span>
      {canEdit && onAccept ? (
        <button className="ew-btn-outline" onClick={onAccept} disabled={accepting} style={{ borderColor: 'var(--ew-performance)', color: 'var(--ew-performance)', whiteSpace: 'nowrap' }}>
          {accepting ? 'Updating…' : `Use ${fmtDate(flag.mobEnd)} as ${phaseLabel} end`}
        </button>
      ) : null}
    </div>
  );
}

/**
 * Shown in the alert's place when accept declined to write.
 *
 * Covers all THREE ways that happens, because they are one story to the reader
 * and the wording has to be true of each: the click-time re-read found the
 * divergence gone; it found a DIFFERENT divergence than the button offered (a
 * different phase or date, which the user never approved); or the server
 * returned 409 because the row changed inside the round trip. Note the change
 * is not always to the phase — editing a mobilization triggers the second case
 * — so the copy says "the schedule", not "this phase".
 *
 * Either way nothing was overwritten and the card has been refreshed. Two of the
 * three paths leave a divergence STILL PRESENT, so this renders alongside the
 * alert rather than instead of it — the copy has to hold with the alert visible
 * directly below, which is why it leads with "your last click wrote nothing"
 * rather than implying the situation is resolved.
 *
 * Without this the alert simply vanished and the click appeared to do nothing —
 * correct, but indistinguishable from a broken button. Informational tone, not a
 * warning: nothing went wrong.
 */
function MobEndResolvedNotice({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div
      role="status"
      style={{ display: 'flex', alignItems: 'center', gap: 'var(--ew-s-2)', flexWrap: 'wrap', padding: 'var(--ew-s-3) var(--ew-s-4)', margin: 'var(--ew-s-4)', borderRadius: 'var(--ew-r-md)', background: 'var(--ew-quality-50)', borderLeft: '3px solid var(--ew-quality)' }}
    >
      <span aria-hidden style={{ color: 'var(--ew-quality)' }}>✓</span>
      <span style={{ font: 'var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font)', color: 'var(--ew-dark)', flex: 1, minWidth: 220 }}>
        Your last click wrote nothing: the schedule had changed while you had this open. Everything below is refreshed — if the dates still disagree, the current figures are shown and you can accept again.
      </span>
      <button
        className="ew-btn-outline"
        onClick={onDismiss}
        style={{ borderColor: 'var(--ew-quality)', color: 'var(--ew-quality)', whiteSpace: 'nowrap' }}
      >
        Dismiss
      </button>
    </div>
  );
}

// ── Card ──────────────────────────────────────────────────────────────────────

export default function PhaseScheduleCard({
  phases, actualsByPhase, actualsMapped, fab, canEdit, onEdit, laborAlert, mobEndFlag, onAcceptMobEnd, acceptingMobEnd,
  mobEndResolved, onDismissMobEndResolved, startMismatch: mismatch,
}: {
  phases: ProjectPhase[];
  actualsByPhase: Map<string, number>;
  actualsMapped: boolean;
  fab: boolean;
  canEdit: boolean;
  onEdit: () => void;
  laborAlert?: LaborStatus | null;
  mobEndFlag?: MobEndFlag | null;
  onAcceptMobEnd?: () => void;
  acceptingMobEnd?: boolean;
  /** Accept found the divergence already resolved by someone else. */
  mobEndResolved?: boolean;
  onDismissMobEndResolved?: () => void;
  /**
   * The crew schedule disagrees with the planned start (#385), or null.
   * Computed by the caller, which is where the crew data lives.
   */
  startMismatch?: StartMismatch | null;
}) {
  // plannedStart counts as data, or a job whose only recorded start is the one it
  // is currently working to would render "No schedule yet" (#380).
  const hasData = phases.length > 0 && phases.some((p) => p.originalStart || p.plannedStart || p.actualStart);

  return (
    <div className="pw-card">
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--ew-s-3)', padding: '14px var(--ew-s-4)', borderBottom: '1px solid var(--ew-n-100)' }}>
        <div>
          <h2 style={{ font: 'var(--ew-fw-semibold) var(--ew-fs-body-sm)/1 var(--ew-font)', letterSpacing: 'var(--ew-ls-caption)', textTransform: 'uppercase', color: 'var(--ew-n-500)', margin: 0 }}>Schedule &amp; Dates</h2>
          <div style={{ font: 'var(--ew-fw-regular) var(--ew-fs-meta) var(--ew-font)', color: 'var(--ew-n-400)', marginTop: 3 }}>
            {fab ? 'Fabrication jobs track two phases — shop fab & field install.' : 'Other divisions use one project phase.'}
          </div>
        </div>
        {canEdit ? <button className="ew-btn-outline" onClick={onEdit}>Edit Schedule</button> : null}
      </div>

      {/*
        ⚠️ OUTSIDE the `hasData` split, so it shows on an empty card too (#385).
        `hasData` asks whether any PHASE carries a date; this mismatch is computed
        from `projects.current_start_date` and the booked crew, neither of which is
        a phase. A job with no saved phase rows gets its seeds from
        `seedPhaseFromLegacy`, and that copies `currentStartDate` into a 'project'
        phase but NOT into the fabrication or installation ones — so a fab job
        holding a current start and no original start reads `hasData === false`
        while the crew grid is flagging it. Nested inside the populated branch,
        this card went silent on exactly those jobs, and the two surfaces
        disagreeing is the one outcome this feature cannot afford.

        It reads correctly on the empty card too: "no schedule recorded, and crew
        booked from the 28th" is a more urgent conversation than the populated
        case, not a less coherent one.
      */}
      {mismatch && <StartMismatchAlert mismatch={mismatch} fab={fab} />}

      {!hasData ? (
        <div style={{ padding: 'var(--ew-s-6)', textAlign: 'center', color: 'var(--ew-n-500)', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)' }}>
          {canEdit
            ? <>No schedule yet. <button className="ew-btn-link" style={{ color: 'var(--ew-green)' }} onClick={onEdit}>Set the schedule →</button></>
            : 'No schedule set.'}
        </div>
      ) : (
        <>
          <PhaseTimeline phases={phases} />
          {/*
            The notice sits ABOVE the alert and is NOT gated on the flag being
            gone. Two of the three paths that set it leave a divergence in place
            — the flag moved to a different phase/date, or a 409 whose refresh
            still shows a mobilization past the plan — so gating on `!mobEndFlag`
            suppressed the confirmation in exactly the cases where the user most
            needs it: the alert visibly CHANGED under them and nothing said their
            click wrote nothing. Reading order is "what just happened", then
            "here is the current situation".
          */}
          {mobEndResolved
            ? <MobEndResolvedNotice onDismiss={onDismissMobEndResolved ?? (() => {})} />
            : null}
          {mobEndFlag ? <MobEndAlert flag={mobEndFlag} canEdit={canEdit} onAccept={onAcceptMobEnd} accepting={acceptingMobEnd} /> : null}
          {/*
            One per offending phase — fab and install are re-planned independently,
            so a single merged message could not say which finish needs moving.
          */}
          {phases.map((p) => {
            const flag = startAfterEnd(p);
            return flag ? (
              <StartAfterEndAlert
                key={`sae-${p.phase}`}
                flag={flag}
                phaseLabel={PHASE_META[p.phase as PhaseKey]?.label ?? p.phase}
                canEdit={canEdit}
                onEdit={onEdit}
              />
            ) : null;
          })}
          {laborAlert?.overrunning ? <LaborAlert labor={laborAlert} /> : null}
          <SlipAlert phases={phases} fab={fab} />
          <div style={{ display: 'grid', gridTemplateColumns: fab ? '1fr 1fr' : '1fr', gap: 'var(--ew-s-4)', padding: 'var(--ew-s-4)' }}>
            {phases.map((p) => (
              <PhaseBlock key={p.phase} phase={p} actualHours={actualsByPhase.get(p.phase) ?? null} actualsMapped={actualsMapped} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
